import { useMemo } from "react";
import { arrayOf, bool, func, object, shape, string } from "prop-types";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import { format } from "date-fns";
import { editor } from "@paths";
import { calculateVisibleLabels, flattenProjectUsers, universalDateParser, parseWorkTypesOrMetadata } from "@utils";
import { useArchivedProjectStore, useCheckProjectAccess } from "@hooks";
import { AvatarList, Checkbox, TableCell, TableRow, TagList } from "@components";
import { Link } from "@material-ui/core";
import { ArchivedMenu } from "./ArchivedMenu";

const WIDTHS = {
  TITLE: 431,
  WORK_TYPES: 320,
  DATE: 80,
  USERS: 130,
  ACTIONS: 34
}

export const ArchivedRow = ({
  uuid,
  name,
  tags,
  metadata,
  createdAt,
  dueOn,
  users,
  onRestore,
  onSelect,
  onRemove,
  selected = false,
}) => {
  const { t } = useTranslation();
  const { isOwner } = useCheckProjectAccess(users);
  const store = useArchivedProjectStore();
  
  const workTypes = useMemo(() => (
    parseWorkTypesOrMetadata(store.workTypes, tags, metadata)
  ), [metadata, tags, store.workTypes]);
  
  const workTypeWidths = useMemo(() => {
    return workTypes.map(workType => {
      const s = document.createElement("span");
      s.innerText = workType.name;
      s.className = "px-2 label-title"
      
      window.document.body.appendChild(s);
      const w = s.getBoundingClientRect().width + 4 + 12; //margins + dot
      window.document.body.removeChild(s);
      return w;
    })
  }, [workTypes, workTypes?.length]);
  
  const vEl = useMemo(() => (
    calculateVisibleLabels(workTypeWidths, WIDTHS.WORK_TYPES - 50)
  ), [workTypeWidths]);
  
  const visibleWorkTypes = workTypes.slice(0, vEl);
  const rest = workTypes.slice(vEl).length;
  
  const handleSelect = () => {
    if (isOwner) onSelect(uuid);
  };

  return (
    <TableRow
      hover
      key={uuid}
      onClick={handleSelect}
      selected={selected}
      className="cursor-pointer"
    >
      <TableCell justifyContent="center" width={26} sticky={0}>
        {isOwner && (
          <Checkbox
            name={uuid}
            aria-label="select row"
            checked={selected}
            color="primary"
            onChange={handleSelect}
            value="true"
          />
        )}
      </TableCell>
      <TableCell width={WIDTHS.TITLE} /*sticky={26 + 24 + 1}*/ noTextOverflow>
        <Link
          className="color-inherit"
          component={RouterLink}
          to={editor(uuid)}
          target="_blank"
          tabIndex={-1}
        >
          {name.replace(/<(.|\n)*?>/g, "")}
        </Link>
      </TableCell>
      {/*<TableCell width={130}>{category ? category.title : ""}</TableCell>*/}
      <TableCell width={WIDTHS.WORK_TYPES}>
        <TagList
          tags={visibleWorkTypes}
          ellipsisOnOverflow
          maxWidth={WIDTHS.WORK_TYPES - 50}
        />
        {
          rest > 0 &&
          <p className="color-primary text-xs ml-1">
            {t("common.more_items", { items: rest })}
          </p>
        }
      </TableCell>
      <TableCell width={WIDTHS.DATE}>
        {format(universalDateParser(createdAt), "dd.MM.yyyy")}
      </TableCell>
      <TableCell width={WIDTHS.DATE}>
        {format(universalDateParser(dueOn), "dd.MM.yyyy")}
      </TableCell>
      <TableCell width={WIDTHS.USERS}>
        <AvatarList users={flattenProjectUsers(users)} />
      </TableCell>
      <TableCell justifyContent="center" width={WIDTHS.ACTIONS}>
        <ArchivedMenu
          uuid={uuid}
          onRestore={onRestore}
          onRemove={onRemove}
          isOwner={isOwner}
        />
      </TableCell>
    </TableRow>
  );
};

ArchivedRow.propTypes = {
  uuid: string.isRequired,
  name: string.isRequired,
  category: object,
  tags: arrayOf(object),
  metadata: string,
  createdAt: string,
  dueOn: string,
  users: shape({
    author: object,
    owner: object,
    observer: arrayOf(object),
  }),

  selected: bool,
  onRestore: func.isRequired,
  onSelect: func.isRequired,
  onRemove: func.isRequired,
};
